import { LitElement, css, html } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';

export interface VirtualGamepadState {
  left: boolean;
  right: boolean;
  up: boolean;
  down: boolean;
  attack: boolean;
  jump: boolean;
  cycleParty: boolean;
}

export interface MobileDebugStats {
  fps: number;
  frameTimeMs: number;
}

const EVENT_NAME = 'himum-virtual-gamepad-change';
const RESOLUTION_CHANGE_EVENT = 'himum-resolution-change';

/** Cheat code to show debug menu: up, up, left, right */
const CHEAT_CODE: Array<'up' | 'down' | 'left' | 'right'> = ['up', 'up', 'left', 'right'];

@customElement('himum-virtual-gamepad')
export class HimumVirtualGamepad extends LitElement {
  /** Whether the gamepad is shown. */
  @property({ type: Boolean, reflect: true }) visible = true;

  @state() private state: VirtualGamepadState = {
    left: false,
    right: false,
    up: false,
    down: false,
    attack: false,
    jump: false,
    cycleParty: false,
  };

  /** Debug menu visibility (hidden until cheat code entered) */
  @state() private debugMenuVisible = false;

  /** Current resolution scale (1 = native, 2 = half, 4 = quarter) */
  @state() private resolutionScale = 1;

  /** Performance stats for debug menu */
  @state() private stats: MobileDebugStats | null = null;

  /** Cheat code input sequence */
  private cheatCodeBuffer: Array<'up' | 'down' | 'left' | 'right'> = [];

  private dpadPointerId: number | null = null;
  private dpadH: 'left' | 'right' | null = null;
  private dpadV: 'up' | 'down' | null = null;
  private attackPointerId: number | null = null;
  private jumpPointerId: number | null = null;
  private cyclePartyPointerId: number | null = null;
  private attemptedOrientationLock = false;

  /** Update stats from external source (called by main.ts) */
  public updateStats(stats: MobileDebugStats): void {
    this.stats = stats;
  }

  static styles = css`
    :host {
      position: fixed;
      inset: 0;
      pointer-events: none;
      z-index: 5000;
      display: block;
      touch-action: none;
      user-select: none;
      -webkit-user-select: none;
    }

    :host([visible="false"]) {
      display: none;
    }

    .root {
      position: absolute;
      inset: 0;
      pointer-events: none;
    }

    .top-right {
      position: absolute;
      top: max(16px, env(safe-area-inset-top));
      right: max(16px, env(safe-area-inset-right));
      pointer-events: auto;
      touch-action: none;
    }

    .debug-menu {
      display: flex;
      flex-direction: column;
      gap: 8px;
      padding: 12px;
      border-radius: 14px;
      background: rgba(0, 0, 0, 0.45);
      border: 1px solid rgba(255, 255, 255, 0.18);
      backdrop-filter: blur(8px);
      -webkit-backdrop-filter: blur(8px);
      box-shadow: 0 10px 22px rgba(0, 0, 0, 0.36);
    }

    .debug-menu-title {
      font-size: 11px;
      font-weight: 700;
      color: rgba(255, 255, 255, 0.6);
      text-transform: uppercase;
      letter-spacing: 0.05em;
      font-family: ui-sans-serif, system-ui, -apple-system, Segoe UI, sans-serif;
    }

    .debug-menu-row {
      display: flex;
      gap: 8px;
    }

    .res-btn {
      min-width: 48px;
      height: 36px;
      border-radius: 10px;
      background: rgba(0, 0, 0, 0.35);
      border: 1px solid rgba(255, 255, 255, 0.18);
      display: grid;
      place-items: center;
      color: rgba(255, 255, 255, 0.88);
      font-size: 13px;
      font-weight: 600;
      font-family: ui-sans-serif, system-ui, -apple-system, Segoe UI, sans-serif;
      transition: transform 60ms linear, background 80ms linear, border-color 80ms linear;
      user-select: none;
      -webkit-user-select: none;
    }

    .res-btn:active {
      transform: scale(0.96);
    }

    .res-btn.active {
      background: rgba(124, 252, 144, 0.25);
      border-color: rgba(124, 252, 144, 0.5);
      color: rgba(124, 252, 144, 1);
    }

    .stats-row {
      display: flex;
      gap: 12px;
      font-size: 12px;
      font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
      color: rgba(255, 255, 255, 0.85);
    }

    .stat-item {
      display: flex;
      gap: 4px;
    }

    .stat-label {
      color: rgba(255, 255, 255, 0.5);
    }

    .stat-value {
      font-weight: 600;
      color: rgba(124, 252, 144, 0.95);
    }

    .pad {
      position: absolute;
      left: max(16px, env(safe-area-inset-left));
      bottom: max(16px, env(safe-area-inset-bottom));
      width: 180px;
      height: 180px;
      pointer-events: auto;
      touch-action: none;
      border-radius: 18px;
      background: transparent;
      border: none;
      backdrop-filter: none;
      box-shadow: none;
    }

    .pad::before {
      content: none;
    }

    .dir {
      position: absolute;
      width: 52px;
      height: 52px;
      border-radius: 14px;
      background: rgba(0, 0, 0, 0.25);
      border: 1px solid rgba(255, 255, 255, 0.18);
      backdrop-filter: blur(6px);
      -webkit-backdrop-filter: blur(6px);
      box-shadow:
        0 12px 24px rgba(0, 0, 0, 0.36),
        0 1px 0 rgba(255, 255, 255, 0.10) inset;
      box-sizing: border-box;
      display: grid;
      place-items: center;
      color: rgba(255, 255, 255, 0.85);
      font-size: 12px;
      font-family: ui-sans-serif, system-ui, -apple-system, Segoe UI, sans-serif;
      pointer-events: none;
      transition: background 80ms linear, border-color 80ms linear;
    }

    .dir.pressed {
      background: rgba(124, 252, 144, 0.18);
      border-color: rgba(124, 252, 144, 0.45);
      color: rgba(124, 252, 144, 0.95);
    }

    .dir.up {
      top: 14px;
      left: 50%;
      transform: translateX(-50%);
    }

    .dir.down {
      bottom: 14px;
      left: 50%;
      transform: translateX(-50%);
    }

    .dir.left {
      left: 14px;
      top: 50%;
      transform: translateY(-50%);
    }

    .dir.right {
      right: 14px;
      top: 50%;
      transform: translateY(-50%);
    }

    .buttons {
      position: absolute;
      right: max(16px, env(safe-area-inset-right));
      bottom: max(16px, env(safe-area-inset-bottom));
      display: grid;
      grid-template-columns: 1fr 1fr;
      grid-template-areas:
        "cycle cycle"
        "attack jump";
      gap: 14px;
      pointer-events: auto;
      touch-action: none;
    }

    .btn.cycle {
      grid-area: cycle;
      justify-self: center;
    }

    .btn.attack {
      grid-area: attack;
    }

    .btn.jump {
      grid-area: jump;
    }

    .btn {
      width: 70px;
      height: 70px;
      border-radius: 999px;
      background: rgba(124, 252, 144, 0.06);
      border: 1px solid rgba(255, 255, 255, 0.18);
      backdrop-filter: blur(6px);
      box-shadow:
        0 14px 28px rgba(0, 0, 0, 0.38),
        0 1px 0 rgba(255, 255, 255, 0.10) inset;
      display: grid;
      place-items: center;
      color: rgba(255, 255, 255, 0.88);
      font-size: 28px;
      font-weight: 600;
      font-family: ui-sans-serif, system-ui, -apple-system, Segoe UI, sans-serif;
      transition: transform 60ms linear, background 80ms linear, border-color 80ms linear;
    }

    .btn .label {
      line-height: 1;
      letter-spacing: 0.02em;
      /* Engraved/debossed look */
      color: rgba(255, 255, 255, 0.78);
      text-shadow:
        0 1px 0 rgba(0, 0, 0, 0.55),
        0 -1px 0 rgba(255, 255, 255, 0.10);
    }

    .btn.pressed {
      background: rgba(124, 252, 144, 0.18);
      border-color: rgba(124, 252, 144, 0.45);
      color: rgba(124, 252, 144, 0.95);
      transform: scale(0.98);
    }

    .btn.pressed .label {
      color: rgba(124, 252, 144, 0.95);
      text-shadow:
        0 1px 0 rgba(0, 0, 0, 0.35),
        0 -1px 0 rgba(255, 255, 255, 0.08);
    }

    .hint {
      display: none;
    }
  `;

  private emit(): void {
    this.dispatchEvent(
      new CustomEvent<VirtualGamepadState>(EVENT_NAME, {
        detail: { ...this.state },
        bubbles: true,
        composed: true,
      })
    );
  }

  private setState(partial: Partial<VirtualGamepadState>): void {
    const next: VirtualGamepadState = { ...this.state, ...partial };
    // Avoid re-emitting if nothing changed.
    const changed =
      next.left !== this.state.left ||
      next.right !== this.state.right ||
      next.up !== this.state.up ||
      next.down !== this.state.down ||
      next.attack !== this.state.attack ||
      next.jump !== this.state.jump ||
      next.cycleParty !== this.state.cycleParty;

    this.state = next;
    if (changed) this.emit();
  }

  private clearDpad(): void {
    this.dpadH = null;
    this.dpadV = null;
    this.setState({ left: false, right: false, up: false, down: false });
  }

  private computeDpadDirectionFromPointer(evt: PointerEvent, padEl: HTMLElement): { nx: number; ny: number } {
    const rect = padEl.getBoundingClientRect();
    const cx = rect.left + rect.width / 2;
    const cy = rect.top + rect.height / 2;

    const dx = evt.clientX - cx;
    const dy = evt.clientY - cy;

    const nx = dx / (rect.width / 2);
    const ny = dy / (rect.height / 2);

    return { nx, ny };
  }

  private updateDpadAxis<TNeg extends string, TPos extends string>(
    current: TNeg | TPos | null,
    value: number,
    negative: TNeg,
    positive: TPos
  ): TNeg | TPos | null {
    // Hysteresis for a "stiff" physical D-pad feel.
    // - Press requires moving beyond pressDeadZone.
    // - Release happens when returning close to center.
    // - Opposite cannot activate unless the axis has been released (returns to null).
    const pressDeadZone = 0.16;
    const releaseDeadZone = 0.10;

    if (current === null) {
      if (Math.abs(value) < pressDeadZone) return null;
      return value < 0 ? negative : positive;
    }

    if (Math.abs(value) < releaseDeadZone) return null;
    // While held, ignore opposite direction until released.
    return current;
  }

  private updateDpadFromPointer(evt: PointerEvent, padEl: HTMLElement): void {
    const { nx, ny } = this.computeDpadDirectionFromPointer(evt, padEl);

    const prevH = this.dpadH;
    const prevV = this.dpadV;

    this.dpadH = this.updateDpadAxis(this.dpadH, nx, 'left', 'right');
    // Note: screen Y increases downward; up is negative.
    this.dpadV = this.updateDpadAxis(this.dpadV, ny, 'up', 'down');

    // Detect newly pressed directions for cheat code
    if (this.dpadH && this.dpadH !== prevH) {
      this.checkCheatCode(this.dpadH);
    }
    if (this.dpadV && this.dpadV !== prevV) {
      this.checkCheatCode(this.dpadV);
    }

    this.setState({
      left: this.dpadH === 'left',
      right: this.dpadH === 'right',
      up: this.dpadV === 'up',
      down: this.dpadV === 'down',
    });
  }

  private tryLockLandscape(): void {
    if (this.attemptedOrientationLock) return;
    this.attemptedOrientationLock = true;

    try {
      const p = (screen as any)?.orientation?.lock?.('landscape');
      // Some browsers/devices reject the promise (or lack support). Swallow to avoid
      // "Uncaught (in promise)" noise.
      if (p && typeof (p as Promise<unknown>).catch === 'function') {
        (p as Promise<unknown>).catch(() => undefined);
      }
    } catch {
      // Best-effort; ignore.
    }
  }

  /** Check and update cheat code buffer when a d-pad direction is pressed */
  private checkCheatCode(direction: 'up' | 'down' | 'left' | 'right'): void {
    this.cheatCodeBuffer.push(direction);
    // Keep only last N entries where N = cheat code length
    if (this.cheatCodeBuffer.length > CHEAT_CODE.length) {
      this.cheatCodeBuffer.shift();
    }
    // Check if buffer matches the code
    if (
      this.cheatCodeBuffer.length === CHEAT_CODE.length &&
      this.cheatCodeBuffer.every((d, i) => d === CHEAT_CODE[i])
    ) {
      this.debugMenuVisible = !this.debugMenuVisible;
      this.cheatCodeBuffer = []; // Reset after toggle
    }
  }

  /** Emit resolution change event */
  private setResolutionScale(scale: number): void {
    this.resolutionScale = scale;
    this.dispatchEvent(
      new CustomEvent<number>(RESOLUTION_CHANGE_EVENT, {
        detail: scale,
        bubbles: true,
        composed: true,
      })
    );
  }

  private onResolutionClick(scale: number) {
    return (e: Event) => {
      e.preventDefault();
      e.stopPropagation();
      this.setResolutionScale(scale);
    };
  }

  private onPadPointerDown = (e: PointerEvent) => {
    e.preventDefault();
    this.tryLockLandscape();

    if (this.dpadPointerId !== null) return; // already controlled

    const padEl = e.currentTarget as HTMLElement;
    this.dpadPointerId = e.pointerId;
    try {
      padEl.setPointerCapture(e.pointerId);
    } catch {
      // ignore
    }

    this.updateDpadFromPointer(e, padEl);
  };

  private onPadPointerMove = (e: PointerEvent) => {
    e.preventDefault();
    if (this.dpadPointerId !== e.pointerId) return;
    const padEl = e.currentTarget as HTMLElement;
    this.updateDpadFromPointer(e, padEl);
  };

  private onPadPointerUp = (e: PointerEvent) => {
    e.preventDefault();
    if (this.dpadPointerId !== e.pointerId) return;
    this.dpadPointerId = null;
    this.clearDpad();
  };

  private onPadPointerCancel = (e: PointerEvent) => {
    e.preventDefault();
    if (this.dpadPointerId !== e.pointerId) return;
    this.dpadPointerId = null;
    this.clearDpad();
  };

  private onAttackPointerDown = (e: PointerEvent) => {
    e.preventDefault();
    this.tryLockLandscape();

    if (this.attackPointerId !== null) return;
    this.attackPointerId = e.pointerId;

    const el = e.currentTarget as HTMLElement;
    try {
      el.setPointerCapture(e.pointerId);
    } catch {
      // ignore
    }

    this.setState({ attack: true });
  };

  private onAttackPointerUp = (e: PointerEvent) => {
    e.preventDefault();
    if (this.attackPointerId !== e.pointerId) return;
    this.attackPointerId = null;
    this.setState({ attack: false });
  };

  private onAttackPointerCancel = (e: PointerEvent) => {
    e.preventDefault();
    if (this.attackPointerId !== e.pointerId) return;
    this.attackPointerId = null;
    this.setState({ attack: false });
  };

  private onJumpPointerDown = (e: PointerEvent) => {
    e.preventDefault();
    this.tryLockLandscape();

    if (this.jumpPointerId !== null) return;
    this.jumpPointerId = e.pointerId;

    const el = e.currentTarget as HTMLElement;
    try {
      el.setPointerCapture(e.pointerId);
    } catch {
      // ignore
    }

    this.setState({ jump: true });
  };

  private onJumpPointerUp = (e: PointerEvent) => {
    e.preventDefault();
    if (this.jumpPointerId !== e.pointerId) return;
    this.jumpPointerId = null;
    this.setState({ jump: false });
  };

  private onJumpPointerCancel = (e: PointerEvent) => {
    e.preventDefault();
    if (this.jumpPointerId !== e.pointerId) return;
    this.jumpPointerId = null;
    this.setState({ jump: false });
  };

  private onCyclePartyPointerDown = (e: PointerEvent) => {
    e.preventDefault();
    this.tryLockLandscape();

    if (this.cyclePartyPointerId !== null) return;
    this.cyclePartyPointerId = e.pointerId;

    const el = e.currentTarget as HTMLElement;
    try {
      el.setPointerCapture(e.pointerId);
    } catch {
      // ignore
    }

    this.setState({ cycleParty: true });
  };

  private onCyclePartyPointerUp = (e: PointerEvent) => {
    e.preventDefault();
    if (this.cyclePartyPointerId !== e.pointerId) return;
    this.cyclePartyPointerId = null;
    this.setState({ cycleParty: false });
  };

  private onCyclePartyPointerCancel = (e: PointerEvent) => {
    e.preventDefault();
    if (this.cyclePartyPointerId !== e.pointerId) return;
    this.cyclePartyPointerId = null;
    this.setState({ cycleParty: false });
  };

  render() {
    const s = this.state;

    return html`
      <div class="root">
        ${this.debugMenuVisible ? html`
          <div class="top-right">
            <div class="debug-menu">
              <div class="stats-row">
                <div class="stat-item">
                  <span class="stat-label">FPS</span>
                  <span class="stat-value">${this.stats ? Math.round(this.stats.fps) : '—'}</span>
                </div>
                <div class="stat-item">
                  <span class="stat-label">ms</span>
                  <span class="stat-value">${this.stats ? this.stats.frameTimeMs.toFixed(1) : '—'}</span>
                </div>
              </div>
              <div class="debug-menu-title">Resolution</div>
              <div class="debug-menu-row">
                <div
                  class="res-btn ${this.resolutionScale === 1 ? 'active' : ''}"
                  @click=${this.onResolutionClick(1)}
                >1x</div>
                <div
                  class="res-btn ${this.resolutionScale === 2 ? 'active' : ''}"
                  @click=${this.onResolutionClick(2)}
                >/2</div>
                <div
                  class="res-btn ${this.resolutionScale === 4 ? 'active' : ''}"
                  @click=${this.onResolutionClick(4)}
                >/4</div>
              </div>
            </div>
          </div>
        ` : ''}

        <div
          class="pad"
          @pointerdown=${this.onPadPointerDown}
          @pointermove=${this.onPadPointerMove}
          @pointerup=${this.onPadPointerUp}
          @pointercancel=${this.onPadPointerCancel}
        >
          <div class="dir up ${s.up ? 'pressed' : ''}">UP</div>
          <div class="dir down ${s.down ? 'pressed' : ''}">DN</div>
          <div class="dir left ${s.left ? 'pressed' : ''}">LT</div>
          <div class="dir right ${s.right ? 'pressed' : ''}">RT</div>
        </div>

        <div class="buttons">
          <div
            class="btn cycle ${s.cycleParty ? 'pressed' : ''}"
            @pointerdown=${this.onCyclePartyPointerDown}
            @pointerup=${this.onCyclePartyPointerUp}
            @pointercancel=${this.onCyclePartyPointerCancel}
          >
            <span class="label">C</span>
          </div>

          <div
            class="btn attack ${s.attack ? 'pressed' : ''}"
            @pointerdown=${this.onAttackPointerDown}
            @pointerup=${this.onAttackPointerUp}
            @pointercancel=${this.onAttackPointerCancel}
          >
            <span class="label">A</span>
          </div>

          <div
            class="btn jump ${s.jump ? 'pressed' : ''}"
            @pointerdown=${this.onJumpPointerDown}
            @pointerup=${this.onJumpPointerUp}
            @pointercancel=${this.onJumpPointerCancel}
          >
            <span class="label">J</span>
          </div>
        </div>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'himum-virtual-gamepad': HimumVirtualGamepad;
  }
}
